import { useEffect, useState, type ReactNode } from 'react';

export type AvisoSection = { key: string; label: string; dot?: 'red' | 'amber' | null; node: ReactNode };

const KEY = 'keora.consola.avisos';
/** En pantallas anchas, estas dos van juntas (anuncios y músicos se usan a la vez). */
const PAIR = ['anuncios', 'musicos'];

/**
 * «Avisos y más» ordenado como Configuración: secciones a la izquierda (con un punto en la que está
 * activa: aviso al aire, mensaje a los músicos o temporizador corriendo) y a la derecha solo el
 * panel elegido, alineado arriba. Se recuerda la sección en esta computadora.
 */
export function AvisosPanel({ sections }: { sections: AvisoSection[] }) {
  const [current, setCurrent] = useState<string>(() => {
    try {
      return localStorage.getItem(KEY) ?? 'anuncios';
    } catch {
      return 'anuncios';
    }
  });
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1600);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 1600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const pick = (k: string) => {
    setCurrent(k);
    try {
      localStorage.setItem(KEY, k);
    } catch {
      // Sin almacenamiento: solo no se recuerda.
    }
  };
  const chosen = sections.find((s) => s.key === current) ?? sections[0]!;
  const shown = wide && PAIR.includes(chosen.key) ? sections.filter((s) => PAIR.includes(s.key)) : [chosen];

  return (
    <div className="avisos">
      <nav className="avisos__nav" aria-label="Secciones de avisos">
        {sections.map((s) => (
          <button key={s.key} type="button" className={`avisos__item${shown.includes(s) ? ' avisos__item--on' : ''}`} aria-pressed={shown.includes(s)} onClick={() => pick(s.key)}>
            <span>{s.label}</span>
            {s.dot ? <span className={`avisos__dot avisos__dot--${s.dot}`} aria-label="Activo" /> : null}
          </button>
        ))}
      </nav>
      <div className={`avisos__panels${shown.length > 1 ? ' avisos__panels--two' : ''}`}>
        {shown.map((s) => (
          <section key={s.key} className="avisos__panel" aria-label={s.label}>
            {s.node}
          </section>
        ))}
      </div>
    </div>
  );
}
